import { t } from "@lingui/macro";
import {
  type ProjectStatusEnum,
  ProjectsAllDocument,
  type ProjectsAllQuery,
  type ProjectsAllQueryVariables,
  ProjectsAllWhereColumn,
  SqlOperator,
} from "@src/__generated__/urql-graphql";
import {
  LargeSelect,
  type LargeSelectProps,
  type LargeSelectRef,
} from "@src/components/ui-kit/Select/LargeSelect";
import { client } from "@src/services/client";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { type FunctionComponent, useEffect, useRef } from "react";

type ProjectSelectProps = Omit<LargeSelectProps, "loadOptions"> & {
  brandFilter?: string[];
  clientFilter?: string[];
  statusFilter?: ProjectStatusEnum[];
};

export const ProjectSelect: FunctionComponent<ProjectSelectProps> = ({
  brandFilter,
  clientFilter,
  statusFilter,
  ...props
}) => {
  const selectRef = useRef<LargeSelectRef>(null);
  const filters = new Filters<ProjectsAllWhereColumn>([
    new Filter({
      column: ProjectsAllWhereColumn.BrandId,
      operator: SqlOperator.In,
      options: [],
      value: brandFilter,
    }),
    new Filter({
      column: ProjectsAllWhereColumn.ClientId,
      operator: SqlOperator.In,
      options: [],
      value: clientFilter,
    }),
    new Filter({
      column: ProjectsAllWhereColumn.Status,
      operator: SqlOperator.Eq,
      options: [],
      value: statusFilter ?? undefined,
    }),
  ]);

  const loadOptions = async (
    page: number,
    perPage: number,
    searchPhrase: string,
  ) => {
    return await client
      .query<ProjectsAllQuery, ProjectsAllQueryVariables>(ProjectsAllDocument, {
        page,
        first: perPage,
        filters: {
          where: filters.asWhereParam,
          search: searchPhrase,
        },
      })
      .toPromise()
      .then(({ data }) => ({
        data: (data?.projectsAll?.data ?? []).map(({ code, title, id }) => ({
          label: `${code} ${title}`,
          value: id,
        })),
        paginatorInfo: data?.projectsAll?.paginatorInfo,
      }));
  };

  useEffect(() => {
    selectRef.current?.clearOptions();
  }, [brandFilter, clientFilter, statusFilter]);

  return (
    <LargeSelect
      ref={selectRef}
      label={t`Projects`}
      loadOptions={loadOptions}
      placeholder={t`Select projects`}
      {...props}
    />
  );
};
